import { Stack, Typography, useTheme } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { DeleteConfirmationModal, Drawer, RecycleIcon } from "components";
import ActionModalCustom from "components/modal/ActionModalCustom";
import { useSelectedRows } from "components/table-v2";
import Table from "components/table-v2/Table";
import {
  useBulkDeleteResources,
  useGetAllResources,
  useGlobalStore,
  useUpdateResourceActive,
} from "controller";
import { memo, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getFullName } from "utilz/helpers";

interface ManageTechDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ResourceData {
  mapping_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  work_role_name: string;
  resource_type_name: string;
  is_active: boolean;
}

const PAGE_SIZE = 50;
type ActionTypes = "mark-as-inactive" | "delete";
const actionButtons = [
  // {
  //   label: "Mark as inactive",
  //   id: "mark-as-inactive",
  //   icon: (
  //     <ClockIcon
  //       fontSize="small"
  //       sx={{ width: "16px", height: "16px" }}
  //       htmlColor="#FFF"
  //     />
  //   ),
  // },
  {
    label: "Delete",
    id: "delete",
    icon: (
      <RecycleIcon
        fontSize="small"
        sx={{ width: "16px", height: "16px" }}
        htmlColor="#FFF"
      />
    ),
  },
];

export const ManageTechDrawer = memo(
  ({ open, onClose }: ManageTechDrawerProps) => {
    const theme = useTheme();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRows, setSelectedRows, removeSelectedRow] =
      useSelectedRows();
    const resourceId = useGlobalStore((state) => state.resourceId);
    const mspOwnerId = useGlobalStore((state) => state.mspOwnerId);
    const { mutateAsync: updateResource, isLoading: isUpdateResourceLoading } =
      useUpdateResourceActive();
    const {
      mutateAsync: deleteResources,
      isLoading: isDeleteResourceInprogress,
    } = useBulkDeleteResources();
    const { data: resources, isFetching: isResourceLoading } =
      useGetAllResources({
        filter: [],
        pageNumber,
        perPageCount: PAGE_SIZE,
        sortColumn: "resources.created_at",
        sortDirection: "desc",
        searchQuery,
      });

    const columns = useMemo<ColumnDef<ResourceData>[]>(
      () => [
        {
          id: "select",
          accessorKey: "select",
          header: "select",
        },
        {
          id: "resource_name",
          accessorKey: "first_name",
          header: "Resource name",
          cell: ({ row }) => {
            const rowData = row.original;
            const fullname = getFullName(
              rowData?.first_name,
              rowData?.middle_name,
              rowData?.last_name,
            );
            return (
              <Typography component="span" sx={{ textTransform: "capitalize" }}>
                {fullname}
              </Typography>
            );
          },
        },
        {
          id: "email",
          accessorKey: "email",
          header: "Email ID",
        },

        {
          id: "work_role_name",
          accessorKey: "work_role_name",
          header: "Billing role",
        },
        {
          id: "resource_type_name",
          accessorKey: "resource_type_name",
          header: "Resource type",
        },
        {
          id: "is_active",
          accessorKey: "is_active",
          header: "Status",
          cell: ({ row }) => (
            <Typography component="span" sx={{ textTransform: "capitalize" }}>
              {row.original?.is_active === true ? "Active" : "Inactive"}
            </Typography>
          ),
        },
      ],
      [],
    );
    const getDisabledButtonIds = () => {
      const disabledButtonIds = [];
      if (selectedRows.selectedIdsArray.length > 1) {
        disabledButtonIds.push("mark-as-inactive");
      }
      return disabledButtonIds;
    };
    const handleActionClick = async (actionId: ActionTypes) => {
      switch (actionId) {
        case "mark-as-inactive":
          await handleMarkAsActive(selectedRows.selectedIdsArray[0]);
          break;
        case "delete":
          setIsDeleteModalOpen(true);
          break;
        default:
          break;
      }
    };
    const onDeleteHandler = async () => {
      const data = selectedRows.selectedIdsArray;
      if (resourceId && Array.isArray(data) && data.includes(resourceId)) {
        toast.error("Trying to delete your own account");
        setIsDeleteModalOpen(false);
        return;
      }

      const resourceIds = {
        resource_ids: data,
      };

      try {
        await deleteResources(resourceIds);
        toast.success("Resources deleted successfully");
        setSelectedRows({});
      } catch (e: any) {
        toast.error(e.message || "Unable delete at the moment");
      }
      setIsDeleteModalOpen(false);
      return;
    };
    const handleMarkAsActive = async (resourceId: string) => {
      try {
        await updateResource({
          resourceId: resourceId,
          isActive: false,
        });
        removeSelectedRow(resourceId);
        toast.success("Resource updated successfully");
      } catch (error: any) {
        toast.error(error || "Something went wrong");
      }
    };

    if (!open) return null;

    return (
      <>
        <Drawer
          open={open}
          title="Manage team members"
          footer={<></>}
          onClose={onClose}
        >
          <Stack
            sx={{
              height: "100%",
              width: "800px",
              p: "8px",
              backgroundColor: theme.palette.neutral["015"],
            }}
          >
            <Stack
              sx={{
                height: "100%",
                width: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#FFF",
                border: `1px solid ${theme.palette.neutral["015"]}`,
                position: "relative",
              }}
            >
              <Table<ResourceData>
                data={
                  Array.isArray(resources?.result)
                    ? (resources.result as ResourceData[])
                    : []
                }
                enableRowSelection={(rowData) =>
                  rowData.original.mapping_id !== mspOwnerId
                }
                columns={columns}
                rowCount={resources?.totalCount || 0}
                // pageIndex={pageNumber}
                pageSize={PAGE_SIZE}
                // onPaginationChange={handlePaginationChange}
                isLoading={isResourceLoading}
                rowSelection={selectedRows.selectedRowsObject}
                onRowSelectionChange={setSelectedRows}
                showActionToolbar={selectedRows?.selectedIdsArray?.length > 0}
                uniqueKey="mapping_id"
                customActionToolbar={
                  <ActionModalCustom
                    open={true}
                    positionMode="parent-relative"
                    onClose={() => console.log("closed")}
                    selectedCount={selectedRows?.selectedIdsArray?.length || 0}
                    selectedText={"Technicians selected"}
                    buttons={actionButtons} // TODO: Add buttons
                    // buttons={getActionButtons}
                    disableButtonIds={getDisabledButtonIds()}
                    // disableButtonIds={[]}
                    onButtonClick={(id) => {
                      handleActionClick(id as ActionTypes);
                    }}
                  />
                }
              />
            </Stack>
          </Stack>
        </Drawer>
        {isDeleteModalOpen && (
          <DeleteConfirmationModal
            open={isDeleteModalOpen}
            onPrimaryButtonClick={onDeleteHandler}
            onSecondaryButtonClick={() => setIsDeleteModalOpen(false)}
            isLoading={isDeleteResourceInprogress}
            title="Delete resources"
            tagline={"Are you sure you want to delete resources"}
          />
        )}
      </>
    );
  },
);

ManageTechDrawer.displayName = "ManageTechDrawer";
